import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { AppCard } from '../../components/ui/AppCard';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { completeSalesforceConnect } from '../../api/dataApi';

// FRONTEND_URL/oauth/callback/salesforce - the one redirect_uri registered
// with the Salesforce connected app, shared by every mapping (see
// modules/mapping/transitions/activity.go's salesforceRedirectURI doc
// comment). Salesforce lands here with either `?code=&state=` (success) or
// `?error=...` (the visitor cancelled/denied consent); this screen's only
// job is to hand code+state to POST /mappings/salesforce/exchange and, on
// success, go back to the mapping SalesforceSetupScreen just created (its
// id was stashed client-side before the redirect - the exchange response
// only carries the nickname, and looking that back up to an id would need
// an extra fetch this round trip doesn't otherwise need).
//
// Unlike OAuthCallbackScreen (social sign-in), this is NOT behind
// RequireGuest - the caller is already a logged-in mapping owner connecting
// an existing integration, not signing in fresh. See App.tsx's routing
// comment for why a static "/oauth/callback/salesforce" route safely
// outranks the guest-only "/oauth/callback/:provider" wildcard.
export function SalesforceOAuthCallbackScreen() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  // StrictMode's dev-only double-render would otherwise run this effect's
  // one-time-use state consumption twice - same fix OAuthCallbackScreen
  // already applies for the identical reason.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const run = async () => {
      const providerError = searchParams.get('error');
      if (providerError) {
        setError('Connecting to Salesforce was cancelled.');
        return;
      }

      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const expectedState = sessionStorage.getItem('oauth.sf.state');
      const mappingId = sessionStorage.getItem('oauth.sf.mappingId');
      sessionStorage.removeItem('oauth.sf.state');
      sessionStorage.removeItem('oauth.sf.mappingId');

      if (!code || !state) {
        setError('This connection link is missing required information.');
        return;
      }
      if (state !== expectedState) {
        setError('This connection link is invalid or was already used. Please try again.');
        return;
      }

      try {
        await completeSalesforceConnect(code, state);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to complete the Salesforce connection.');
        return;
      }
      navigate(mappingId ? `/integrations/${mappingId}` : '/integrations');
    };

    run();
  }, [searchParams, navigate]);

  if (!error) {
    return (
      <div className="flex flex-col gap-4">
        <AppCard>
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <Loader2 size={28} className="animate-spin text-black/40 dark:text-white/40" />
            <span className="text-sm text-black/50 dark:text-white/50">Finishing Salesforce connection…</span>
          </div>
        </AppCard>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <AppCard>
        <div className="flex flex-col items-center gap-4">
          <EmptyState icon={AlertCircle} title="Connection didn't work" subtitle={error} />
          <Button full onClick={() => navigate('/integrations/catalog')}>
            Back to connectors
          </Button>
        </div>
      </AppCard>
    </div>
  );
}
